import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {DjPsycho} from "../DjPsycho";
import {TimelineLite} from "gsap";
import {
    ISimpleReelModelEventData,
    ISimpleReelModelState,
    SimpleReelModel
} from "../../core/reel/simplereel/SimpleReelModel";
import {DjPsychoReelsView} from "../reels/DjPsychoReelsView";
import {ArrayUtils} from "../utils/ArrayUtils";
import {DjPsychoGameMode} from "../server/DjPsychoGameMode";
import {DjPsychoSound} from "../sound/DjPsychoSound";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class DjPsychoNormalResultPresentation implements FlowStatePresentation {
    private _tl: TimelineLite | null;
    private _stoppedReels: boolean[];
    private _soundPlayed: boolean;
    private _isAborted: boolean;

    public start(flowStateController: FlowStateController): void {
        const mode: string = DjPsycho.model.data.mode,
            tl: TimelineLite = new TimelineLite();

        // #136
        flowStateController.setSkipCallback();
        this._soundPlayed = false;
        this._isAborted = false;

        // #52
        if (mode === DjPsychoGameMode.NORMAL && DjPsycho.sfsBought && DjPsycho.model.gameData.wasFeatureBuy) {
            for (let ctr: number = 0; ctr < (DjPsycho.rowsNum + 1); ctr++) {
                tl.add(DjPsycho.miniCollectors.activateColorIndicators(ctr, false), 0);
            }
            tl.add(() => {
                DjPsycho.miniCollectors.switchToSFSEnabled();
            }, 0);

            tl.add(() => {
                this.onCompletePresentation(flowStateController);
            });

            return;
        }

        if (mode === DjPsychoGameMode.NORMAL && DjPsycho.model.gameData.wasFeatureBuy) {
            tl.to(this, {
                duration: 0.5
            });
            tl.add(DjPsycho.bonusBuy.indicateLuckyDraw());
            tl.add([
                () => {
                    // #156
                    DjPsycho.soundPlayer.playSound(DjPsychoSound.FS_BUY_METER_HIGHLIGHT);
                },
                DjPsycho.bonusBuy.playCollectorIndicatorSpine()
            ]);
            tl.addLabel("bonusBuyAnimated");
            tl.add(DjPsycho.miniCollectors.activateTexts(), "bonusBuyAnimated");
            for (let ctr: number = 0; ctr < (DjPsycho.rowsNum + 1); ctr++) {
                tl.add(DjPsycho.miniCollectors.activateColorIndicators(ctr, false), "bonusBuyAnimated");
            }
            tl.add(() => {
                this.onCompletePresentation(flowStateController);
            });

            return;
        }

        // #107
        DjPsycho.bonusBuy.stopAll();

        this._stoppedReels = [false, false, false, false];

        const minSpinTime: number = DjPsycho.apiPlugin.gameClientConfiguration.minimumSpinTime / 1000;

        let delay: number = DjPsychoReelsView.stepDuration * 2;
        // adding this to fulfil the condition of minimum spin times in Jurisdictions.
        if (mode === DjPsychoGameMode.NORMAL && minSpinTime > 0) {
            delay = (minSpinTime / DjPsycho.rowsNum);
        }

        const isAvalanche: boolean = mode.indexOf("AVALANCHE") > -1,
            isFastSpin: boolean = DjPsycho.apiPlugin.settings.get(APISetting.FAST_SPIN, false);

        // #53
        if (isFastSpin && !minSpinTime) {
            delay = 0;
        }

        tl.add(() => {
            for (let i: number = 0; i < DjPsycho.reelsNum; i++) {
                const reelData: string[] = DjPsycho.model.gameData.reels[i];
                DjPsycho.reelView.reelModels[i].stop(reelData);
                DjPsycho.reelView.reelModels[i].events?.once(ISimpleReelModelState.APPEAR, (data: ISimpleReelModelEventData) => this.onReelStop(data, flowStateController));
            }

            DjPsycho.hotZone.hideHotZone();

            if (isAvalanche) {
                DjPsycho.soundPlayer.playSound(DjPsychoSound.BET_LINES_ADD);
            }

            //#136
            if (!isAvalanche) {
                flowStateController.setSkipCallback(() => {
                    this.onSkip();
                });
            }
        }, delay);
        if (mode === DjPsychoGameMode.NORMAL) {
            tl.add(DjPsycho.miniCollectors.activateTexts(), delay);
            if (minSpinTime === 0) {
                tl.to(this, {
                    duration: 0.1
                });
            }
        }

        const reelTl: TimelineLite = new TimelineLite();

        for (let idx: number = 0; idx < DjPsycho.rowsNum; idx++) {
            DjPsycho.reelView.reelModels.forEach((reel: SimpleReelModel) => {
                reelTl.add(reel.appearSymbolAt(idx), (isAvalanche ? delay : (delay * (idx + 1))));
            });

            if (!isAvalanche) {
                if (!delay && idx === 0) {
                    reelTl.add(() => {
                        DjPsycho.soundPlayer.playSound(DjPsychoSound.ROW_LANDING);
                    }, delay);
                } else if (delay) {
                    reelTl.add(() => {
                        // #136
                        if (!this._soundPlayed) {
                            DjPsycho.soundPlayer.playSound(DjPsychoSound.ROW_LANDING);
                        }

                        // #136
                        if (this._isAborted) {
                            this._soundPlayed = true;
                        }
                    }, (delay * (idx + 1)));
                }

                if (idx === (DjPsycho.rowsNum - 1)) {
                    reelTl.add(() => {
                        DjPsycho.soundPlayer.fadeOutEffect({
                            name: DjPsychoSound.REELS_SPINNING,
                            duration: 300,
                            to: 0
                        });
                    }, (delay * (idx + 1)));
                }
            }
        }
        reelTl.add(() => {
            DjPsycho.reelView.reelModels.forEach((reel: SimpleReelModel) => {
                reel.state = ISimpleReelModelState.APPEAR; // to make the reel stop finish.
                reel.state = ISimpleReelModelState.IDLE; // next state is idle for reel.
            });
        });

        tl.add(reelTl);

        this._tl = tl;
    }

    private onReelStop(data: ISimpleReelModelEventData, flowStateController: FlowStateController): void {
        this._stoppedReels[data.reel] = true;
        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            this.onCompletePresentation(flowStateController);
        }
    }

    private onCompletePresentation(flowStateController: FlowStateController): void {
        flowStateController.finish();
        this._tl = null;
    }

    // #136
    private onSkip(): void {
        if (this._tl) {
            this._isAborted = true;
            this._tl.totalProgress(1);
        }
    }
}